// The top bar search and its suggestions layer (WEBSITE feature requirements
// 3.2). The field is the 400 px control with the "/" key cap; the popover is
// a combobox listbox with the four required groups, each carrying the reason
// it is available or empty.
//
// Behaviour bound by the contract: suggestions appear only while the field is
// focused and the text clears the server's 2 character floor; arrows move the
// highlight and Enter opens it; Escape closes the popover and keeps the text;
// an outside click or a completed navigation closes it; an ambiguous archive
// answer lists the candidate contracts instead of guessing. Text past the
// endpoint's 120 character limit keeps the popover open and names the limit,
// because a pasted market link is long and still parses to a contract.
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { SEARCH } from '../api/config';
import { useArchiveSearch, useSearchEntities } from '../api/hooks';
import type { WorkspaceRole } from '../api/types';
import { useView } from '../router/useView';
import { SearchField } from '../ui/Input';
import { cx } from '../ui/cx';
import { callHash, writeCallHash } from './openCall';
import {
  buildSearchSections,
  flattenSuggestions,
  moveHighlight,
  type LoadedRecordSource,
  type SearchAction,
  type SearchSuggestion,
} from './searchModel';

const MAX_PER_GROUP = 5;

export interface GlobalSearchProps {
  readonly loaded: LoadedRecordSource;
  readonly role?: WorkspaceRole | null;
}

// Debounces the network reads without delaying what the reader typed: the
// field stays instant, the two requests wait for the typing to settle.
function useSettled(value: string, delayMs: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

export function GlobalSearch({ loaded, role = null }: GlobalSearchProps) {
  const { view, navigate } = useView();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  const settled = useSettled(query, SEARCH.debounceMs);
  const trimmed = query.trim();
  // The two server searches take 2 to 120 characters. Under the floor there
  // is nothing to show; over the ceiling the popover stays and says so.
  const tooLong = trimmed.length > SEARCH.maxChars;
  const unsent = tooLong
    ? `Not sent: ${trimmed.length} characters, the limit is ${SEARCH.maxChars}. Paste the contract or the ticker on its own.`
    : null;
  const open = focused && trimmed.length >= SEARCH.minChars && !dismissed;

  const entities = useSearchEntities(settled, { enabled: open && !tooLong });
  const archive = useArchiveSearch(settled, { enabled: open && !tooLong });

  const sections = useMemo(
    () =>
      buildSearchSections({
        query,
        loaded,
        archive: {
          data: archive.data,
          isPending: archive.isPending,
          isPlaceholder: archive.isPlaceholderData,
          error: archive.error,
        },
        entities: {
          data: entities.data,
          isPending: entities.isPending,
          isPlaceholder: entities.isPlaceholderData,
          error: entities.error,
        },
        maxPerGroup: MAX_PER_GROUP,
        serverUnsentReason: unsent,
        role,
      }),
    [
      query,
      loaded,
      unsent,
      role,
      archive.data,
      archive.isPending,
      archive.isPlaceholderData,
      archive.error,
      entities.data,
      entities.isPending,
      entities.isPlaceholderData,
      entities.error,
    ],
  );

  const options = useMemo(() => (open ? flattenSuggestions(sections) : []), [open, sections]);

  // The first enabled option is the default target, so Enter always opens a
  // real result instead of routing the raw text somewhere.
  const activeId =
    highlightId !== null && options.some((option) => option.id === highlightId)
      ? highlightId
      : (options[0]?.id ?? null);

  const close = useCallback((restoreFocus: boolean) => {
    setDismissed(true);
    setHighlightId(null);
    if (restoreFocus) inputRef.current?.focus();
  }, []);

  const run = useCallback(
    (action: SearchAction) => {
      if (action.kind === 'call') {
        // Same split as the tape: in place on Live Calls, handed over as the
        // whole hash from anywhere else, so no filter travels with the call id.
        if (view === 'live') writeCallHash(action.callId);
        else navigate('live', { hash: callHash(action.callId) });
      } else {
        navigate(action.view, action.q === undefined ? {} : { q: action.q });
      }
      setDismissed(true);
      setHighlightId(null);
      inputRef.current?.blur();
    },
    [navigate, view],
  );

  const openSuggestion = useCallback(
    (suggestion: SearchSuggestion) => {
      if (suggestion.disabled || suggestion.action === null) return;
      run(suggestion.action);
    },
    [run],
  );

  // An outside click closes the popover; the text stays for the next focus.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent): void => {
      if (
        rootRef.current &&
        event.target instanceof Node &&
        !rootRef.current.contains(event.target)
      )
        close(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape') {
      if (!open) return;
      // The shell's Escape order would blur the field; the popover closes first.
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      close(true);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!open) {
        setDismissed(false);
        return;
      }
      event.preventDefault();
      setHighlightId(moveHighlight(activeId, event.key === 'ArrowDown' ? 1 : -1, options));
      return;
    }
    if (event.key === 'Enter') {
      const target = options.find((option) => option.id === activeId);
      if (!target) return;
      event.preventDefault();
      openSuggestion(target);
      return;
    }
    if (event.key === 'Tab' && open) close(false);
  };

  return (
    <div ref={rootRef} className="search" role="search">
      <SearchField
        ref={inputRef}
        slashFocus
        value={query}
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && activeId ? `${listId}-${activeId}` : undefined}
        aria-autocomplete="list"
        aria-label="Global search, press slash"
        autoComplete="off"
        onChange={(event) => {
          setQuery(event.target.value);
          setDismissed(false);
          setHighlightId(null);
        }}
        onFocus={() => {
          setFocused(true);
          setDismissed(false);
        }}
        onBlur={() => setFocused(false)}
        onKeyDown={onKeyDown}
      />
      {open ? (
        <div className="sug" id={listId} role="listbox" aria-label="Search suggestions">
          {sections.map((section) => (
            <div
              className="sug-grp"
              key={section.id}
              role="group"
              aria-labelledby={`${listId}-${section.id}`}
              aria-busy={section.state === 'loading' || undefined}
            >
              <div className="sug-grp-h">
                <span id={`${listId}-${section.id}`} className="sug-grp-t">
                  {section.title}
                </span>
                <span className="sug-grp-why">{section.reason}</span>
              </div>
              {section.items.map((item) => (
                <div
                  key={item.id}
                  id={`${listId}-${item.id}`}
                  role="option"
                  aria-selected={item.id === activeId}
                  aria-disabled={item.disabled || undefined}
                  className={cx(
                    'sug-row',
                    item.id === activeId && 'sug-row-on',
                    item.disabled && 'sug-row-off',
                  )}
                  onMouseEnter={() => {
                    if (!item.disabled) setHighlightId(item.id);
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    openSuggestion(item);
                  }}
                >
                  <span className={cx('sug-nm', item.mono && 'mono')}>{item.label}</span>
                  {item.detail ? <span className="sug-dt">{item.detail}</span> : null}
                  <span className="sug-why">{item.availability}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
